import { STR } from '../i18n';
import type { ChipTone, DotTone } from '../ui/Primitives';

/**
 * R3 — APPLICATION HEALTH IS SELF-REPORTED, AND "NEVER CHECKED" IS NOT A FAULT.
 *
 * `lastStatus` is whatever the application last told us, or null because nothing has told us
 * anything yet. Nothing in this wave writes these values — the poller is a later round — so
 * this maps what is stored and invents nothing. Maintenance is warn-toned and visually
 * distinct from unreachable, which is bad-toned; a null status makes NO status claim at all,
 * and an unrecognised value is reported as unrecognised rather than guessed at.
 */

export interface HealthPresentation {
  readonly dot: DotTone;
  readonly chip: ChipTone;
  readonly label: string;
  readonly note: string;
}

/**
 * The LAUNCHER's reading of the same enum. It lives here, beside `healthOf`, because there
 * is one home for "what does this status mean" — but it is a separate function because the
 * launcher genuinely says something different: the admin screen reports a fact about an
 * application, while this decides whether to encourage or discourage opening it.
 *
 * `chip` and `note` are null together for any status this app makes NO claim about. Both the
 * never-checked state and an unrecognised value get that treatment: saying nothing is honest,
 * and inventing a presentation for a value we do not understand is not.
 */
export interface LaunchAffordance {
  readonly dot: DotTone;
  readonly chip: { readonly tone: ChipTone; readonly label: string } | null;
  readonly note: string | null;
  /** Launch stays available in every state — discouraged, never disabled. */
  readonly buttonClass: 'prim' | 'ghost';
  readonly buttonLabel: string;
}

export function launchAffordanceOf(lastStatus: string | null): LaunchAffordance {
  switch (lastStatus) {
    case 'healthy':
      return {
        dot: 'ok',
        chip: { tone: 'positive', label: STR.applications.health.healthyLabel },
        note: null,
        buttonClass: 'prim',
        buttonLabel: STR.launcher.open,
      };
    case 'maintenance':
      return {
        dot: 'warn',
        chip: { tone: 'attention', label: STR.applications.health.maintenanceLabel },
        note: STR.launcher.maintenanceNote,
        buttonClass: 'ghost',
        buttonLabel: STR.launcher.openAnyway,
      };
    case 'unreachable':
      return {
        dot: 'bad',
        chip: { tone: 'danger', label: STR.applications.health.unreachableLabel },
        note: STR.launcher.unreachableNote,
        buttonClass: 'ghost',
        buttonLabel: STR.launcher.tryAnyway,
      };
    default:
      // Never checked (null) AND anything unrecognised: a neutral dot, no chip, no note, and
      // a plain launch. Nothing writes these fields until the Phase 3 poller, so today this
      // is what almost every application renders as.
      return {
        dot: 'neutral',
        chip: null,
        note: null,
        buttonClass: 'prim',
        buttonLabel: STR.launcher.open,
      };
  }
}

export function healthOf(lastStatus: string | null): HealthPresentation {
  switch (lastStatus) {
    case 'healthy':
      return {
        dot: 'ok',
        chip: 'positive',
        label: STR.applications.health.healthyLabel,
        note: STR.applications.health.healthyNote,
      };
    case 'maintenance':
      return {
        dot: 'warn',
        chip: 'attention',
        label: STR.applications.health.maintenanceLabel,
        note: STR.applications.health.maintenanceNote,
      };
    case 'unreachable':
      return {
        dot: 'bad',
        chip: 'danger',
        label: STR.applications.health.unreachableLabel,
        note: STR.applications.health.unreachableNote,
      };
    case null:
      return {
        dot: 'neutral',
        chip: 'neutral',
        label: STR.applications.health.neverLabel,
        note: STR.applications.health.neverNote,
      };
    default:
      return {
        dot: 'neutral',
        chip: 'neutral',
        label: STR.applications.health.unknownLabel,
        note: STR.applications.health.unknownNote,
      };
  }
}
